import React, { useMemo } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  ReferenceLine
} from 'recharts';
import { TrendingUp, AlertTriangle } from 'lucide-react';

const fmtMicros = (v) => v != null ? `$${(v / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-';

export default function ForecastOverlayWidget({ forecast, loading, error, activeCampaignCount }) {
  const chartData = useMemo(() => {
    if (!forecast) return [];
    const historical = (forecast.historical_daily || []).map(d => ({
      date: d.date_utc,
      actual: d.commission_micros,
    }));
    const projected = (forecast.daily_projection || []).map(d => ({
      date: d.date_utc,
      projected: d.projected_commission_micros,
    }));
    return [...historical, ...projected];
  }, [forecast]);

  const hasCampaigns = activeCampaignCount > 0;

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="forecast-overlay-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#3BA4C5]" />
            Revenue Forecast
          </CardTitle>
          <div className="flex gap-2">
            {forecast && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200">
                {forecast.model || 'linear_trend'}
              </Badge>
            )}
            {hasCampaigns && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-200 text-emerald-600">
                {activeCampaignCount} active campaign{activeCampaignCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="h-52 bg-slate-50 animate-pulse rounded" data-testid="forecast-loading" />
        )}

        {!loading && error && (
          <div className="py-6 flex flex-col items-center gap-2" data-testid="forecast-error">
            <AlertTriangle className="h-5 w-5 text-amber-400" />
            <p className="text-sm text-amber-600">Forecast unavailable</p>
            <p className="text-[10px] text-slate-400">PostgreSQL connection required for revenue data</p>
          </div>
        )}

        {!loading && !error && forecast && (
          <>
            {/* Forecast KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="forecast-kpis">
              <div className="bg-slate-50 rounded-md px-3 py-2">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Window</p>
                <p className="text-sm font-bold text-[#122033] font-[Manrope]">{forecast.historical_window_days}d</p>
              </div>
              <div className="bg-blue-50 rounded-md px-3 py-2">
                <p className="text-[10px] text-blue-500 uppercase tracking-wider">Moving Avg</p>
                <p className="text-sm font-bold text-blue-700 font-[Manrope]">{fmtMicros(forecast.moving_average)}/d</p>
              </div>
              <div className="bg-emerald-50 rounded-md px-3 py-2">
                <p className="text-[10px] text-emerald-500 uppercase tracking-wider">30d Forecast</p>
                <p className="text-sm font-bold text-emerald-700 font-[Manrope]">{fmtMicros(forecast.forecast_next_30_total)}</p>
              </div>
              <div className={`rounded-md px-3 py-2 ${forecast.slope >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                <p className={`text-[10px] uppercase tracking-wider ${forecast.slope >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>Trend</p>
                <p className={`text-sm font-bold font-[Manrope] ${forecast.slope >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {forecast.slope >= 0 ? '+' : ''}{fmtMicros(forecast.slope)}/d
                </p>
              </div>
            </div>

            {/* Campaign Overlay Note */}
            {hasCampaigns && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2 mb-3 flex items-center gap-2" data-testid="forecast-campaign-note">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                <p className="text-[11px] text-emerald-700">
                  {activeCampaignCount} active campaign{activeCampaignCount !== 1 ? 's' : ''} may boost projected revenue.
                  Campaign-adjusted forecast reflects current momentum.
                </p>
              </div>
            )}

            {/* Chart */}
            {chartData.length > 0 && (
              <div data-testid="forecast-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 9, fill: '#94A3B8' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(d) => {
                        const dt = new Date(d);
                        return `${dt.getMonth()+1}/${dt.getDate()}`;
                      }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#94A3B8' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `$${(v/100).toFixed(0)}`}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 11, borderRadius: 8 }}
                      formatter={(v, name) => [fmtMicros(v), name === 'actual' ? 'Actual' : 'Projected']}
                      labelFormatter={(d) => formatDateLocale(d)}
                    />
                    <Line type="monotone" dataKey="actual" stroke="#122033" strokeWidth={2} dot={false} name="actual" />
                    <Line type="monotone" dataKey="projected" stroke="#3BA4C5" strokeWidth={2} strokeDasharray="5 5" dot={false} name="projected" />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex gap-4 mt-2 justify-center" data-testid="forecast-legend">
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    <div className="w-4 h-0.5 bg-[#122033] rounded" />
                    <span>Historical</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    <div className="w-4 h-0.5 bg-[#3BA4C5] rounded" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #3BA4C5 0, #3BA4C5 4px, transparent 4px, transparent 8px)' }} />
                    <span>Projected</span>
                  </div>
                </div>
              </div>
            )}

            {/* Model description */}
            <p className="text-[10px] text-slate-300 mt-3 text-center" data-testid="forecast-model-desc">
              {forecast.model_description}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
